import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";

const LOGO = "/tresses_logo_full.png";

export default function ResetPasswordPage() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const token = params.get("token") || "";
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const onSubmit = async (e) => {
        e.preventDefault();
        if (password.length < 8) {
            toast.error("Password must be at least 8 characters");
            return;
        }
        if (password !== confirm) {
            toast.error("Passwords don't match");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/auth/reset-password", { token, new_password: password });
            toast.success("Password updated — please sign in with your new password");
            navigate("/login");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail, "Couldn't reset password"));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center px-6 py-12 grain bg-[#FBF6F0]" data-testid="reset-password-page">
            <div className="w-full max-w-md">
                <Link to="/" className="flex items-center gap-4 mb-12">
                    <img src={LOGO} alt="The Tresses — Unisex Salon" className="h-32 w-auto object-contain" data-testid="auth-logo" />
                </Link>

                {!token ? (
                    <div className="card-luxe grain p-8" data-testid="reset-missing-token">
                        <div className="text-overline text-red-700">Invalid Link</div>
                        <h1 className="font-serif-display text-2xl text-[#3D0C1F] mt-3">Missing or broken reset link.</h1>
                        <p className="text-sm text-[#7A5262] mt-4">Please request a new password reset link.</p>
                        <Link to="/forgot-password" className="btn-gold w-full justify-center mt-6">
                            Request a new link
                        </Link>
                    </div>
                ) : (
                    <>
                        <div className="text-overline">Reset Password</div>
                        <h1 className="font-serif-display text-4xl text-[#3D0C1F] mt-3">Choose a new password.</h1>
                        <p className="text-sm text-[#7A5262] mt-2">Use at least 8 characters.</p>

                        <form onSubmit={onSubmit} className="mt-10 space-y-5" data-testid="reset-password-form">
                            <div>
                                <label className="text-overline">New password</label>
                                <input
                                    type="password"
                                    required
                                    minLength={8}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="w-full bg-[#FDF0EC] border border-[#6B1535]/15 px-4 py-3 mt-2 focus:border-[#C29A5B] outline-none text-[#3D0C1F]"
                                    placeholder="••••••••"
                                    data-testid="reset-password"
                                />
                            </div>
                            <div>
                                <label className="text-overline">Confirm new password</label>
                                <input
                                    type="password"
                                    required
                                    minLength={8}
                                    value={confirm}
                                    onChange={(e) => setConfirm(e.target.value)}
                                    className="w-full bg-[#FDF0EC] border border-[#6B1535]/15 px-4 py-3 mt-2 focus:border-[#C29A5B] outline-none text-[#3D0C1F]"
                                    placeholder="••••••••"
                                    data-testid="reset-confirm"
                                />
                            </div>
                            <button type="submit" disabled={submitting} className="btn-gold w-full justify-center disabled:opacity-50" data-testid="reset-submit">
                                {submitting ? "Updating…" : "Update password"}
                            </button>
                        </form>
                    </>
                )}

                <div className="text-center mt-6 pt-6 border-t border-[#6B1535]/10">
                    <Link to="/" className="text-[0.65rem] tracking-[0.22em] uppercase text-[#7A5262] hover:text-[#8F622C]" data-testid="reset-back-home">
                        ← Back to home
                    </Link>
                </div>
            </div>
        </div>
    );
}
